import React, { useState } from 'react';
import { MapPin, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { projectsData } from '../mockData';
import Footer from '../components/Footer';

const ProjectsPage = () => {
  const [showAll, setShowAll] = useState(false);

  // ✅ Sort projects by completion date
  const sortedProjects = [...projectsData].sort((a, b) => {
    return new Date(b.completionDate) - new Date(a.completionDate);
  });

  // ✅ Split into two groups: with images / without images
  const projectsWithImages = sortedProjects.filter((p) => p.image);
  const projectsWithoutImages = sortedProjects.filter((p) => !p.image);

  const styles = `.section-title {
  text-align: center;
}
.section-title h2 {
  font-size: 3rem;
  font-weight: 700;
}
.heading-underline {
  width: 80px;
  height: 6px;
  margin: 0 auto 1.2rem auto;
  background: #2992cc;
  border-radius: 4px;
}
.heading-tagline {
  color: #7a7a7a;
  font-size: 1.5rem;
  font-weight: 400;
  margin-top: .5rem;
  margin-bottom: 1.5rem;
  letter-spacing: 0;
  line-height: 1.3;
}
.gap {
  margin-bottom: 2.5rem;
}
`;

  const visibleProjects = showAll
    ? projectsWithImages
    : projectsWithImages.slice(0, 6);

  return (
    <div className="min-h-screen">
      <style>{styles}</style>

      {/* 🔹 Hero Section */}
      <section
        className="relative flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: "url(/images/project_banner.png)",
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          height: "90vh",
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 max-w-5xl px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-6">
            <span className="bg-white bg-clip-text text-transparent drop-shadow-lg">
              Projects
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed">
            Explore our portfolio of successfully completed residential
            construction projects that showcase our commitment to quality,
            innovation, and customer satisfaction.
          </p>
        </div>
      </section>

      {/* 🔹 Projects with Images */}
      <section className="w-full">
        <div className="gap"></div>

        {visibleProjects.map((project, idx) => (
          <div
            key={project.id}
            className={`w-full flex flex-col md:flex-row ${idx % 2 === 1 ? 'md:flex-row-reverse' : ''
              } min-h-[380px]`}
            style={{
              background:
                idx % 2 === 0
                  ? 'linear-gradient(90deg, #f7faff 60%, #f4f9fd 100%)'
                  : 'linear-gradient(90deg, #FFF 60%, #e7f3fb 100%)',
            }}
          >
            {/* Image Side */}
            {project.image && (
              <div className="md:w-1/2 w-full">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full object-cover md:object-fill"
                  style={{ height: '512px', display: 'block' }}
                />
              </div>
            )}

            {/* Content Side */}
            <div className="md:w-1/2 w-full flex flex-col justify-center p-10">
              {project.id === 1 ? (
                // ID = 1 IMAGE
                <img
                  src={project.titleImg}
                  alt="Project Title"
                  className="h-[85px] md:h-[95px] lg:h-[120px] 
               w-[320px] md:w-[380px] lg:w-[420px]
               object-contain drop-shadow-xl mb-4"
                />
              ) : project.id === 2 ? (
                
                <img
                  src={project.titleImg}
                  alt="Project Title"
                  className="h-[80px] md:h-[85px] lg:h-[120px] 
               w-[260px] md:w-[320px] lg:w-[330px]
               drop-shadow-xl mb-4"
                />
              ) : (
                <h3 className="text-2xl md:text-4xl font-extrabold text-gray-900 mb-2 tracking-tight">
                  {project.title}
                </h3>
              )}
              <div className="w-20 h-1 bg-gradient-to-r from-[#011321] to-[#03365f] rounded mb-6"></div>

              <div className="flex flex-wrap items-center gap-4 mb-6">
                {project.location && (
                  <a
                    className="flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-medium text-base no-underline hover:text-[#11b0e7]"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      project.location
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    {project.location}
                  </a>
                )}
                {project.completionDate && (
                  <span className="flex items-center px-4 py-2 rounded-full bg-gray-200 text-gray-700 font-medium text-base">
                    <Calendar className="w-4 h-4 mr-2" />
                    {new Date(project.completionDate).getFullYear()}
                  </span>
                )}
                {project.status && (
                  <span className="flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-[#211e88] to-[#11b0e7] text-white font-medium text-base">
                    {project.status}
                  </span>
                )}
              </div>

              <p className="text-[14px] md:text-lg text-gray-700 max-w-2xl mb-10">
                {project.description}
              </p>

              <div className="flex flex-col sm:flex-row sm:gap-4 gap-2">
                <Link
                  to={`/project/${project.id}`}
                  className="inline-flex items-center justify-center px-6 py-2 bg-gradient-to-r from-[#011321] to-[#03365f] text-white text-[15px] md:text-lg font-semibold rounded-full shadow-lg hover:scale-105 duration-200 transition-all w-[200px] sm:w-auto"
                >
                  Explore Project
                  <span className="ml-2">&rarr;</span>
                </Link>

                {project.brochureURL && (
                  <a
                    href={project.brochureURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-2 bg-gradient-to-r from-[#011321] to-[#03365f] text-white text-[15px] md:text-lg font-semibold rounded-full border border-[#011321] hover:bg-gray-200 transition-all duration-300 w-[200px] sm:w-auto"
                  >
                    Download E-brochure
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* View More / Show Less Button */}
        {projectsWithImages.length > 6 && (
          <div className="text-center mt-10 px-6 py-8">
            <button
              onClick={() => setShowAll(!showAll)}
              className="px-6 py-3 bg-black text-white font-semibold rounded-full hover:scale-105 transition-all duration-300"
            >
              {showAll ? 'Show Less' : 'View More'}
            </button>
          </div>
        )}
      </section>

      {/* 🔹 Show "Other Projects" only after View More */}
      {showAll && projectsWithoutImages.length > 0 && (
        <section className="max-w-5xl mx-auto my-16 px-6">
          <h3 className="text-3xl font-bold mb-6 text-center">
            Other Projects
          </h3>
          <ul className="space-y-4">
            {projectsWithoutImages.map((project) => (
              <li
                key={project.id}
                className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-gray-300 pb-3"
              >
                {/* LEFT COLUMN */}
                <span className="
        text-lg font-semibold text-gray-800
        w-full md:w-1/2 
        text-left 
        pl-4 md:pl-8 lg:pl-12 xl:pl-16
      ">
                  {project.title}
                </span>

                {/* RIGHT COLUMN */}
                {project.location && (
                  <span className="
          text-gray-600 flex items-center gap-2
          w-full md:w-1/2 
          text-left
          pl-4 md:pl-10 lg:pl-20 xl:pl-40 2xl:pl-60
        ">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    {project.location}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default ProjectsPage;
